import { Information16 } from '@carbon/icons-react';
import { Tooltip } from 'antd';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  CpBody00Secondary,
  CpHeading03Primary,
} from 'components/atoms/customer-portal/typography';
import { Caption01 } from 'components/styles';
import { tryEmojify } from 'utils/emojify';
import { tryGetTextFromHTML } from 'utils/getTextFromHTML';
import { useGetBackground } from '../../types/utils';
import { CpWidgetProps } from '../../types/widget';
import { CpSectionWrapper } from '../common';
import { CpWidgetHeader } from '../common/header';

interface CpSummaryInformationRenderInput extends Record<string, unknown> {
  fields: {
    fieldName: string;
    fieldLabel: string;
    fieldValue: unknown;
    description: string;
  }[];
  title: string;
  description: string;
}

export const SummaryCardInfoCardsWrapper = styled.div`
  display: flex;
  align-items: start;
  justify-content: start;
  gap: var(--cp-spacing-06);
  flex-wrap: wrap;
  width: 100%;

  @media (max-width: 480px) {
    gap: var(--cp-spacing-05);
    flex-direction: column;
  }
`;

export const SummaryInfoCardWrapper = styled.div`
  max-width: 100%;
  width: 340px;
`;
export const SummaryInfoCardDescriptionIcon = styled(Information16)`
  color: var(--cp-text-secondary);
`;
export const SummaryInfoCardTitle = styled.div`
  display: flex;
  align-items: center;
  ${SummaryInfoCardDescriptionIcon} {
    flex-shrink: 0;
    margin-left: var(--cp-spacing-03);
  }
`;

export const SummaryInfoCard = styled.div`
  height: 100%;
  width: 100%;
  height: 136px;
  min-height: 136px;
  max-height: 136px;
  box-shadow: var(--shadow-02);
  background-color: var(--white-100);
  display: flex;
  align-items: start;
  justify-content: end;
  flex-direction: column;
  padding: 0 var(--cp-spacing-06) var(--cp-spacing-06);
  @media (min-width: 769px) {
    ${SummaryInfoCardDescriptionIcon} {
      visibility: hidden;
    }
    &:hover ${SummaryInfoCardDescriptionIcon} {
      visibility: visible;
    }
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

export const SummaryInfoCardLabel = styled(CpBody00Secondary)`
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

export const SummaryInfoCardDescription = styled.div`
  ${Caption01};
  color: var(--cp-text-placeholder);
  padding-right: var(--cp-spacing-06);
`;

export const SummaryInfoCardValue = styled(CpHeading03Primary)`
  margin-bottom: 0;
  text-align: left;
  /* ellipsis */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 80%;
  margin-top: var(--cp-spacing-02);
`;

export const StyledSummaryInforCardValueForProjectDate = styled(
  SummaryInfoCardValue,
)`
  font-size: var(--cp-heading-4-font-size);
  margin-top: var(--cp-spacing-04);
`;

export const CpSummaryInformation: React.FC<CpWidgetProps> = ({
  widget,
  disabled,
  data: _data,
}) => {
  const data = useMemo(() => _data as CpSummaryInformationRenderInput, [_data]);

  const background: string = useGetBackground(widget);

  return (
    <CpSectionWrapper
      background={background}
      paddingY={
        widget?.widgetDefinition?.styles?.paddingY as number | undefined
      }
      isDisabled={disabled}
      className="!cp-max-w-none"
    >
      <ContentWrapper className="!cp-w-full !cp-items-center lg:!cp-items-start !cp-h-full lg:!cp-h-full">
        <CpWidgetHeader>
          <CpWidgetHeader.Title
            className="rl-cp-summary-info-section-title"
            data-cy="cp.summary-info.title"
            autoAdjustColor
          >
            {data?.title}
          </CpWidgetHeader.Title>
          <CpWidgetHeader.Description
            autoAdjustColor
            data-cy="cp.summary-info.description"
            className="rl-cp-summary-info-section-sub-title"
          >
            {data?.description}
          </CpWidgetHeader.Description>
        </CpWidgetHeader>
      </ContentWrapper>
      <SummaryCardInfoCardsWrapper>
        {data?.fields?.map?.((e, index) => (
          <SummaryInfoCardWrapper
            data-cy={`cp.summary-info.${e?.fieldName}.wrapper`}
            data-cy-project-summary-info-index={index}
          >
            <SummaryInfoCard>
              <SummaryInfoCardTitle>
                <SummaryInfoCardLabel
                  data-cy={`cp.summary-info.${e?.fieldName}.label`}
                >
                  {e?.fieldLabel}
                </SummaryInfoCardLabel>
                {e?.description ? (
                  <Tooltip
                    title={e.description}
                    data-cy={`cp.summary-info.${e?.fieldName}.description`}
                  >
                    <SummaryInfoCardDescriptionIcon />
                  </Tooltip>
                ) : (
                  <></>
                )}
              </SummaryInfoCardTitle>
              <Tooltip
                title={
                  e?.fieldValue
                    ? tryGetTextFromHTML(tryEmojify(e.fieldValue as string))
                    : undefined
                }
                placement="topLeft"
              >
                <SummaryInfoCardValue
                  data-cy={`cp.summary-info.${e?.fieldName}.value`}
                >
                  {tryGetTextFromHTML(
                    tryEmojify((e.fieldValue as string) ?? '-'),
                  )}
                </SummaryInfoCardValue>
              </Tooltip>
            </SummaryInfoCard>
          </SummaryInfoCardWrapper>
        ))}
      </SummaryCardInfoCardsWrapper>
    </CpSectionWrapper>
  );
};
